import { useContext, useEffect, useMemo, useRef } from 'react';

import {
  createIndirectTaxRegistration,
  requestIndirectTaxRegistrationsValidationBulk,
  updateIndirectTaxRegistration,
  updateIndirectTaxRegistrationCalculationSetting,
  updateIndirectTaxRegistrationsStatusBulk,
  updateIndirectTaxRegistrationFormData,
  useIndirectTaxAvailableRegistrations,
  useIndirectTaxRegistrationJurisdictions,
  useIndirectTaxRegistrationPortalFields,
  useIndirectTaxRegistrationTaxTypeDetails,
} from '@commenda/api/indirect-tax/registrations';
import {
  createIndirectTaxCredentialsBulk,
  listIndirectTaxCredentials,
  updateIndirectTaxCredential,
  useIndirectTaxCredentials,
  IndirectTaxCredentialBlockType,
  IndirectTaxCredentialType,
} from '@commenda/api/new-sales-tax/credentials';
import {
  IndirectTaxJurisdictionType,
  TIndirectTaxRegisteredBy,
  TIndirectTaxRegistrationStatus,
  type IIndirectTaxTypeDetails,
  type IIndirectTaxUpdateRegistrationFormDataParams,
} from '@commenda/api/indirect-tax/registrations/types';
import { updateRemittanceAccount } from '@commenda/api/bankAccount';
import { useCorporationSettings } from '@commenda/api/new-sales-tax/corporations';
import { Button } from '@commenda/kakashi';

import { IndirectTaxRegion } from '../../../../indirect-tax/common/types';
import { IndirectTaxRegistrationSelector } from '../../../../indirect-tax/registrations/components/IndirectTaxRegistrationSelector';
import { IndirectTaxRegistrationCredentialsStep } from '../../../../indirect-tax/registrations/components/IndirectTaxRegistrationCredentialsStep';
import { IndirectTaxRegistrationPortalCard } from '../../../../indirect-tax/registrations/components/IndirectTaxRegistrationPortalCard';
import { trimIndirectTaxCredentialFieldValue } from '../../../../indirect-tax/registrations/utils/trimIndirectTaxCredentialFieldValue';
import { IndirectTaxRegistrationHelper } from '../../../../indirect-tax/registrations/utils/IndirectTaxRegistrationHelper';
import { UKPresenceType } from '../../../../indirect-tax/registrations/wizards/new-state/constants';
import {
  UKActivityAndPresenceSection,
  validateUKActivity,
} from './UKActivityAndPresenceSection';
import { FormContext } from '../../../hooks/FormContext';
import { strOrUndef } from '../../../utils/context-values';

import { DEFAULT_JURISDICTION_CONTEXT_KEY } from './types';
import type {
  IndirectTaxRegistrationConfig,
  IndirectTaxRegistrationValue,
} from './types';

interface IndirectTaxRegistrationComponentProps {
  fieldId?: string;
  props?: IndirectTaxRegistrationConfig;
}

const IndirectTaxRegistrationComponent = ({
  fieldId,
  props,
}: IndirectTaxRegistrationComponentProps) => {
  const {
    formData,
    updateField,
    dslContext,
    isPreview,
    registerSubmitHandler,
  } = useContext(FormContext);

  const jurisdictionContextKey =
    props?.jurisdictionContextKey ?? DEFAULT_JURISDICTION_CONTEXT_KEY;

  const isNewRegistration = props?.newRegistration != null;

  const corporationId = dslContext?.corporationId;
  const jurisdictionId = strOrUndef(dslContext?.[jurisdictionContextKey]);
  const contextRegistrationId = strOrUndef(dslContext?.registrationId);

  const registrationRegion =
    IndirectTaxRegistrationHelper.getRegistrationRegion({
      jurisdictionId: jurisdictionId ?? '',
    });

  const value = (fieldId ? formData[fieldId] : undefined) as
    | IndirectTaxRegistrationValue
    | undefined;

  const {
    data: availableRegistrations,
    isLoading: isLoadingAvailableRegistrations,
    error: availableRegistrationsError,
  } = useIndirectTaxAvailableRegistrations(
    corporationId != null && jurisdictionId
      ? { corporationId, jurisdictionIds: [jurisdictionId] }
      : null
  );

  const {
    data: taxTypeDetailsData,
    error: taxTypeDetailsError,
  } = useIndirectTaxRegistrationTaxTypeDetails(
    corporationId != null && jurisdictionId
      ? { corporationId, jurisdictionIds: [jurisdictionId] }
      : null
  );

  const { data: jurisdictionsData } = useIndirectTaxRegistrationJurisdictions(
    corporationId != null ? { corporationId, limit: 100 } : null
  );

  const { data: corporationSettingsData } = useCorporationSettings({
    corporationId: corporationId ?? 0,
  });

  const jurisdictionLookup = useMemo(() => {
    const map = new Map<
      string,
      {
        country: string;
        state?: string;
        jurisdictionType: IndirectTaxJurisdictionType;
      }
    >();
    jurisdictionsData?.jurisdictions.forEach((j) => {
      map.set(j.jurisdictionId, {
        country: j.country,
        state: j.state,
        jurisdictionType: j.jurisdictionType,
      });
    });
    return map;
  }, [jurisdictionsData]);

  const taxTypeDetailsMap = useMemo(() => {
    const map: Record<string, IIndirectTaxTypeDetails> = {};
    taxTypeDetailsData?.taxTypeDetails?.forEach((detail) => {
      map[detail.taxType] = detail;
    });
    return map;
  }, [taxTypeDetailsData]);

  const registrations = useMemo(
    () => availableRegistrations ?? [],
    [availableRegistrations]
  );

  const contextRegistration = useMemo(
    () =>
      registrations.find((r) => r.registrationId === contextRegistrationId) ??
      null,
    [registrations, contextRegistrationId]
  );

  const selectedRegistrationId =
    contextRegistration?.registrationId ??
    value?.contentRegistrationId ??
    '';

  const selectedRegistration = useMemo(
    () =>
      registrations.find((r) => r.registrationId === selectedRegistrationId) ??
      null,
    [registrations, selectedRegistrationId]
  );

  const selectedTaxTypeOption = useMemo(
    () =>
      selectedRegistration?.taxTypeOptions.find(
        (option) => option.registrationTaxTypeId === value?.registrationTaxTypeId
      ) ?? null,
    [selectedRegistration, value?.registrationTaxTypeId]
  );

  const portalIds = useMemo(
    () => selectedTaxTypeOption?.portalIds ?? [],
    [selectedTaxTypeOption]
  );

  const { data: portalFieldsData, isLoading: isLoadingPortals } =
    useIndirectTaxRegistrationPortalFields(
      corporationId != null && portalIds.length > 0
        ? { corporationId, portalIds }
        : null
    );

  const portals = portalFieldsData?.portals ?? [];

  // Mirror the wizard: auto-select the sole portal.
  useEffect(() => {
    if (!fieldId) return;
    if (value?.portalId) return;
    if (portals.length !== 1 || !portals[0]) return;
    updateField(fieldId, { ...(value ?? {}), portalId: portals[0].portalId });
  }, [portals, value, fieldId, updateField]);

  const selectedPortal = useMemo(
    () => portals.find((p) => p.portalId === value?.portalId) ?? null,
    [portals, value?.portalId]
  );

  const { data: credentialsByPortal, isLoading: isCheckingCredentials } =
    useIndirectTaxCredentials(
      corporationId != null && selectedPortal?.portalId
        ? { corporationId, portalIds: [selectedPortal.portalId] }
        : null
    );

  const credentialsFound = (credentialsByPortal?.length ?? 0) > 0;

  const prefilledCredentialIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (!fieldId) return;
    const existingCredential = credentialsByPortal?.[0];
    if (!selectedPortal || !existingCredential?.id) return;
    if (prefilledCredentialIdRef.current === existingCredential.id) return;

    const credentialLevelKeys = new Set(
      (selectedPortal.credentialFields ?? [])
        .filter((f) => !f.isUniquePerRegistration)
        .map((f) => f.credentialKey)
    );
    const prefillCredentialFields: Record<string, string> = {};
    (existingCredential.blocks ?? []).forEach((block) => {
      if (block.value && credentialLevelKeys.has(block.blockKey)) {
        prefillCredentialFields[block.blockKey] = block.value;
      }
    });

    prefilledCredentialIdRef.current = existingCredential.id;
    if (Object.keys(prefillCredentialFields).length === 0) return;
    updateField(fieldId, {
      ...(value ?? {}),
      credentialFields: {
        ...prefillCredentialFields,
        ...(value?.credentialFields ?? {}),
      },
    });
  }, [selectedPortal, credentialsByPortal, value, fieldId, updateField]);

  const patch = (next: Partial<IndirectTaxRegistrationValue>) => {
    if (!fieldId) return;
    updateField(fieldId, { ...(value ?? {}), ...next });
  };

  useEffect(() => {
    if (!fieldId || !registrationRegion) return;
    if (value?.jurisdictionRegion === registrationRegion) return;
    updateField(fieldId, {
      ...(value ?? {}),
      jurisdictionRegion: registrationRegion,
    });
  }, [registrationRegion, value, fieldId, updateField]);

  const patchAndResetCredentials = (
    next: Partial<IndirectTaxRegistrationValue>
  ) => {
    prefilledCredentialIdRef.current = null;
    patch({
      ...next,
      registrationFields: undefined,
      credentialFields: undefined,
    });
  };

  const setTaxTypeOption = (
    registrationTaxTypeId: string,
    taxTypes: string[]
  ) =>
    patchAndResetCredentials({
      registrationTaxTypeId,
      taxTypes,
      frequency: undefined,
      portalId: undefined,
    });

  // Mirror the wizard: pre-select the sole tax type option.
  useEffect(() => {
    if (!fieldId) return;
    if (value?.registrationTaxTypeId) return;
    if (!selectedRegistration) return;
    if (selectedRegistration.taxTypeOptions.length !== 1) return;
    const onlyOption = selectedRegistration.taxTypeOptions[0];
    if (!onlyOption) return;
    updateField(fieldId, {
      ...(value ?? {}),
      registrationTaxTypeId: onlyOption.registrationTaxTypeId,
      taxTypes: IndirectTaxRegistrationHelper.getRequiredTaxTypes(onlyOption),
      frequency: undefined,
    });
  }, [selectedRegistration, value, fieldId, updateField]);

  const latestRef = useRef({
    value,
    formData,
    selectedTaxTypeOption,
    selectedRegistration,
    selectedPortal,
    jurisdictionLookup,
    corporationSettingsData,
    registrationRegion,
  });
  latestRef.current = {
    value,
    formData,
    selectedTaxTypeOption,
    selectedRegistration,
    selectedPortal,
    jurisdictionLookup,
    corporationSettingsData,
    registrationRegion,
  };

  // Writes the registration itself (sales-tax-api), not via Commenda OS submit.
  useEffect(() => {
    if (!fieldId || isPreview || corporationId == null) return;
    const taxRegistrationNumber = strOrUndef(
      dslContext?.taxRegistrationNumber
    );
    // The request this form runs in, which the registration made here claims.
    const serviceRequestId = strOrUndef(dslContext?.serviceRequestId);
    registerSubmitHandler?.(fieldId, async () => {
      const {
        value: currentValue,
        selectedTaxTypeOption: currentTaxTypeOption,
        selectedPortal: currentPortal,
      } = latestRef.current;
      const isNewRegistrationSubmit = props?.newRegistration != null;
      if (
        !currentValue?.registrationTaxTypeId ||
        !currentValue.taxTypes?.length ||
        (!isNewRegistrationSubmit &&
          (!currentValue.frequency || !currentValue.effectiveStartDate))
      ) {
        throw new Error(
          isNewRegistrationSubmit
            ? 'Select a registration and tax type to continue.'
            : 'Select a registration, tax type, filing frequency, and effective date to continue.'
        );
      }
      if (
        isNewRegistrationSubmit &&
        latestRef.current.registrationRegion ===
          IndirectTaxRegion.EUROPEAN_UNION &&
        (currentValue.intraCommunityTrade == null ||
          currentValue.importsOrExportsGoods == null)
      ) {
        throw new Error(
          'Answer the cross-border trade questions to continue.'
        );
      }
      if (
        isNewRegistrationSubmit &&
        latestRef.current.registrationRegion ===
          IndirectTaxRegion.UNITED_KINGDOM
      ) {
        const ukError = validateUKActivity(currentValue.ukActivity);
        if (ukError) throw new Error(ukError);
      }

      const toBlockType = (credentialType: string) =>
        credentialType === 'SecureString'
          ? IndirectTaxCredentialBlockType.SecureString
          : credentialType === 'File'
            ? IndirectTaxCredentialBlockType.File
            : IndirectTaxCredentialBlockType.String;

      let credentialIdToUse: string | undefined;
      if (!isNewRegistrationSubmit && currentPortal) {
        const credentialsResp = await listIndirectTaxCredentials({
          corporationId,
          portalIds: [currentPortal.portalId],
        });
        const currentCredentials = credentialsResp.data?.credentials;
        const currentCredentialsFound = (currentCredentials?.length ?? 0) > 0;
        const credentialLevelFields =
          currentPortal.credentialFields?.filter(
            (f) => !f.isUniquePerRegistration
          ) ?? [];
        const existingCredential = currentCredentials?.[0];
        if (currentCredentialsFound && existingCredential?.id) {
          if (credentialLevelFields.length > 0) {
            const blocks = credentialLevelFields.map((field) => {
              const existingBlock = existingCredential.blocks?.find(
                (b) => b.blockKey === field.credentialKey
              );
              return {
                ...(existingBlock?.id ? { id: existingBlock.id } : {}),
                blockKey: field.credentialKey,
                blockLabel: field.credentialName,
                blockType: toBlockType(field.credentialType),
                value: trimIndirectTaxCredentialFieldValue(
                  currentValue.credentialFields?.[field.credentialKey] || '',
                  field.credentialType
                ),
              };
            });
            const updateResp = await updateIndirectTaxCredential(
              existingCredential.id,
              { corporationId, portalId: currentPortal.portalId, blocks }
            );
            if (!updateResp.success) {
              throw new Error(
                updateResp.error ?? 'Failed to update credentials.'
              );
            }
          }
          credentialIdToUse = existingCredential.id;
        } else if (!currentCredentialsFound) {
          const blocks = credentialLevelFields
            .map((field) => ({
              blockKey: field.credentialKey,
              blockLabel: field.credentialName,
              blockType: toBlockType(field.credentialType),
              value: trimIndirectTaxCredentialFieldValue(
                currentValue.credentialFields?.[field.credentialKey] || '',
                field.credentialType
              ),
              required: field.required,
            }))
            .filter((block) => block.required || block.value !== '')
            .map(({ required: _required, ...block }) => block);
          if (blocks.length > 0) {
            const credResp = await createIndirectTaxCredentialsBulk({
              corporationId,
              credentials: [
                {
                  credentialType:
                    IndirectTaxCredentialType.INDIRECT_TAX_REGISTRATION,
                  portalId: currentPortal.portalId,
                  name: currentPortal.portalName || 'Portal credential',
                  portalUrl: currentPortal.portalUrl?.trim() || undefined,
                  blocks,
                },
              ],
            });
            if (!credResp.success || !credResp.data?.credentials?.length) {
              throw new Error(
                credResp.error ?? 'Failed to create credentials.'
              );
            }
            credentialIdToUse = credResp.data.credentials[0]?.id;
          }
        }
      }

      const registrationLevelFields =
        !isNewRegistrationSubmit && currentPortal
          ? (currentPortal.credentialFields?.filter(
              (f) => f.isUniquePerRegistration
            ) ?? [])
          : [];
      let resolvedTaxRegistrationNumber = taxRegistrationNumber;
      const filledRegistrationField = registrationLevelFields.find(
        (field) =>
          trimIndirectTaxCredentialFieldValue(
            currentValue.registrationFields?.[field.credentialKey] || '',
            field.credentialType
          ) !== ''
      );
      if (filledRegistrationField) {
        resolvedTaxRegistrationNumber = trimIndirectTaxCredentialFieldValue(
          currentValue.registrationFields?.[filledRegistrationField.credentialKey] || '',
          filledRegistrationField.credentialType
        );
      }

      const selectedFrequencyOption = currentTaxTypeOption?.frequencies.find(
        (f) => f.primary === currentValue.frequency
      );
      const requiredRelatedFrequencies =
        selectedFrequencyOption?.related
          ?.filter((related) => !related.optional)
          .map((related) => related.value) ?? [];
      const frequencies = isNewRegistrationSubmit
        ? []
        : [
            currentValue.frequency as string,
            ...requiredRelatedFrequencies,
          ];
      let registrationId = currentValue.createdTaxRegistrationId;
      if (!registrationId) {
        const { success, data, error } = await createIndirectTaxRegistration({
          corporationId,
          registrationTaxTypeId: currentValue.registrationTaxTypeId,
          taxTypes: currentValue.taxTypes,
          frequencies,
          effectiveStartDate: currentValue.effectiveStartDate,
          registeredBy: isNewRegistrationSubmit
            ? TIndirectTaxRegisteredBy.COMMENDA
            : TIndirectTaxRegisteredBy.USER,
          credentialId: credentialIdToUse || undefined,
          taxRegistrationNumber: resolvedTaxRegistrationNumber,
          serviceRequestId,
        });
        if (!success || !data?.registration?.id) {
          throw new Error(error ?? 'Failed to create registration');
        }
        registrationId = data.registration.id;
        updateField(fieldId, {
          ...currentValue,
          createdTaxRegistrationId: registrationId,
        });
      } else {
        const { success, error } = await updateIndirectTaxRegistration({
          registrationId,
          corporationId,
          body: {
            taxTypes: currentValue.taxTypes,
            ...(frequencies.length > 0 ? { frequencies } : {}),
            effectiveStartDate: currentValue.effectiveStartDate,
            credentialId: credentialIdToUse || undefined,
            taxRegistrationNumber: resolvedTaxRegistrationNumber,
          },
        });
        if (!success) {
          throw new Error(error ?? 'Failed to update registration');
        }
      }
      const submittedValue = {
        ...currentValue,
        createdTaxRegistrationId: registrationId,
      };
      const {
        formData: currentFormData,
        selectedRegistration: currentRegistration,
        jurisdictionLookup: currentJurisdictionLookup,
        corporationSettingsData: currentCorporationSettings,
      } = latestRef.current;

      const newRegistration = props?.newRegistration;
      if (newRegistration) {

        const bankFieldValue = newRegistration.bankAccountFieldId
          ? (currentFormData?.[newRegistration.bankAccountFieldId] as
              | { selectedId?: string | null; country?: string | null }
              | undefined)
          : undefined;
        if (newRegistration.bankAccountFieldId && !bankFieldValue?.selectedId) {
          throw new Error('Select a remittance bank account to continue.');
        }
        if (bankFieldValue?.selectedId) {
          const { success: remittanceSuccess } = await updateRemittanceAccount(
            bankFieldValue.selectedId,
            { isRemittanceAccount: true }
          );
          if (!remittanceSuccess) {
            throw new Error('Failed to set the remittance bank account');
          }
        }

        const responsiblePartyValue = newRegistration.responsiblePartyFieldId
          ? (currentFormData?.[newRegistration.responsiblePartyFieldId] as
              | { selectedIds?: string[] }
              | undefined)
          : undefined;
        const officerId = responsiblePartyValue?.selectedIds?.[0]
          ? Number(responsiblePartyValue.selectedIds[0])
          : undefined;

        const currency = currentRegistration?.jurisdictionId
          ? IndirectTaxRegistrationHelper.getRegistrationCurrencyCode({
              jurisdictionId: currentRegistration.jurisdictionId,
            })
          : 'USD';

        const formDataPatch: IIndirectTaxUpdateRegistrationFormDataParams = {
          corporationId,
          currency,
        };
        if (officerId != null && !Number.isNaN(officerId)) {
          formDataPatch.officerId = officerId;
        }
        if (newRegistration.effectiveStartDateFieldId) {
          formDataPatch.effectiveStartDateOfTaxAccount = strOrUndef(
            currentFormData?.[newRegistration.effectiveStartDateFieldId]
          );
        }
        if (newRegistration.firstYearTaxableSaleFieldId) {
          const sales = Number(
            currentFormData?.[newRegistration.firstYearTaxableSaleFieldId]
          );
          if (!Number.isNaN(sales)) formDataPatch.firstYearTaxableSale = sales;
        }
        if (newRegistration.reasonForNonUKBankAccountFieldId) {
          const reason = strOrUndef(
            currentFormData?.[newRegistration.reasonForNonUKBankAccountFieldId]
          );
          const isNonUKBankAccount =
            latestRef.current.registrationRegion ===
          IndirectTaxRegion.UNITED_KINGDOM &&
            bankFieldValue?.country != null &&
            bankFieldValue.country !== 'GB';
          if (isNonUKBankAccount && !reason) {
            throw new Error(
              'Select a reason for not having a UK bank account to continue.'
            );
          }
          formDataPatch.reasonForNonUKBankAccount = isNonUKBankAccount
            ? (reason ?? null)
            : null;
        }
        if (currentValue.intraCommunityTrade != null) {
          formDataPatch.intraCommunityTrade = currentValue.intraCommunityTrade;
        }
        if (currentValue.importsOrExportsGoods != null) {
          formDataPatch.importsOrExportsGoods = currentValue.importsOrExportsGoods;
        }
        const { ukActivity } = currentValue;
        if (ukActivity) {
          formDataPatch.ukVATRegistrationReasons =
            ukActivity.ukVATRegistrationReasons ?? [];
          formDataPatch.ukActivityDescription =
            ukActivity.ukActivityDescription;
          formDataPatch.goodsStoredOrShippedFromUK =
            ukActivity.goodsStoredOrShippedFromUK;
          formDataPatch.ukGoodProviders =
            ukActivity.goodsStoredOrShippedFromUK === true
              ? (ukActivity.ukGoodProviders ?? [])
              : null;
          formDataPatch.hasUKExemptSupplies = ukActivity.hasUKExemptSupplies;
          formDataPatch.ukPresenceTypes = ukActivity.ukPresenceTypes ?? [];
          const hasNonNonePresence =
            ukActivity.ukPresenceTypes?.some(
              (presence) => presence !== UKPresenceType.NONE
            ) ?? false;
          formDataPatch.ukPresenceDetails = hasNonNonePresence
            ? ukActivity.ukPresenceDetails
            : null;
          formDataPatch.directorsInvolvedInOtherUKBusiness =
            ukActivity.directorsInvolvedInOtherUKBusiness;
          formDataPatch.otherUKBusinessesDetails =
            ukActivity.directorsInvolvedInOtherUKBusiness === true
              ? ukActivity.otherUKBusinessesDetails
              : null;
        }
        const { success: formDataSuccess, error: formDataError } =
          await updateIndirectTaxRegistrationFormData(
            registrationId,
            formDataPatch
          );
        if (!formDataSuccess) {
          throw new Error(
            formDataError ?? 'Failed to save registration details'
          );
        }
      }

      const { success: managedFilingSuccess, error: managedFilingError } =
        await updateIndirectTaxRegistration({
          registrationId,
          corporationId,
          body: { managedFilingEnabled: true },
        });
      if (!managedFilingSuccess) {
        throw new Error(managedFilingError ?? 'Failed to enable managed filing');
      }

      const jurisdictionInfo = currentRegistration?.jurisdictionId
        ? currentJurisdictionLookup.get(currentRegistration.jurisdictionId)
        : undefined;
      if (
        jurisdictionInfo?.country === 'US' &&
        jurisdictionInfo.jurisdictionType ===
          IndirectTaxJurisdictionType.STATE_OR_PROVINCE &&
        jurisdictionInfo.state
      ) {
        const existingJurisdictionSetting =
          currentCorporationSettings?.data?.find(
            (entry) =>
              entry.country === 'US' && entry.state === jurisdictionInfo.state
          );
        const { success: calculationSettingSuccess, error: calculationSettingError } =
          await updateIndirectTaxRegistrationCalculationSetting({
            corporationId,
            country: 'US',
            state: jurisdictionInfo.state,
            disableTaxCalculation: false,
            currentManageFilings: true,
            currentExemptionExpiration:
              existingJurisdictionSetting?.data
                ?.disable_exemptions_after_expiration,
          });
        if (!calculationSettingSuccess) {
          throw new Error(
            calculationSettingError ?? 'Failed to update tax calculation setting'
          );
        }
      }

      const { success: statusSuccess, error: statusError } =
        await updateIndirectTaxRegistrationsStatusBulk({
          corporationId,
          registrationIds: [registrationId],
          registrationStatus: newRegistration
            ? TIndirectTaxRegistrationStatus.REGISTRATION_IN_PROGRESS
            : TIndirectTaxRegistrationStatus.REGISTERED,
        });
      if (!statusSuccess) {
        throw new Error(statusError ?? 'Failed to update registration status');
      }

      await requestIndirectTaxRegistrationsValidationBulk({
        corporationId,
        registrationIds: [registrationId],
      });

      return submittedValue;
    });
  }, [
    registerSubmitHandler,
    fieldId,
    isPreview,
    corporationId,
    updateField,
    dslContext,
  ]);

  if (corporationId == null || !jurisdictionId) {
    return (
      <p className="txt-small-regular text-text-secondary">
        {corporationId == null
          ? 'No company entity selected.'
          : 'No jurisdiction selected.'}
      </p>
    );
  }

  if (availableRegistrationsError || taxTypeDetailsError) {
    return (
      <p className="txt-small-regular text-text-error">
        Failed to load registrations for this region.
      </p>
    );
  }

  const showCredentials = selectedPortal != null && portals.length > 0;

  return (
    <div className="flex flex-col gap-2xl">
      <IndirectTaxRegistrationSelector
      availableRegistrations={registrations}
      isLoading={isLoadingAvailableRegistrations}
      selectedRegistrationId={selectedRegistrationId}
      onRegistrationChange={(registrationId) =>
        patchAndResetCredentials({
          contentRegistrationId: registrationId,
          registrationTaxTypeId: undefined,
          taxTypes: [],
          frequency: undefined,
          portalId: undefined,
        })
      }
      selectedTaxTypeOption={selectedTaxTypeOption}
      selectedRegistrationTaxTypeId={value?.registrationTaxTypeId ?? ''}
      selectedTaxTypes={value?.taxTypes ?? []}
      onTaxTypeOptionChange={setTaxTypeOption}
      onOptionalTaxTypeToggle={(taxType, checked) => {
        const current = value?.taxTypes ?? [];
        patch({
          taxTypes: checked
            ? [...current, taxType]
            : current.filter((t) => t !== taxType),
        });
      }}
      selectedFrequency={value?.frequency ?? ''}
      onFrequencyChange={(frequency) => patch({ frequency })}
      effectiveStartDate={value?.effectiveStartDate ?? ''}
      onEffectiveDateChange={(effectiveStartDate) =>
        patch({ effectiveStartDate })
      }
      taxTypeDetailsMap={taxTypeDetailsMap}
      isRegistrationLocked={!!contextRegistration}
      registrationName={contextRegistration?.registrationName}
      hideFrequencyAndDate={isNewRegistration}
    />

      {!isNewRegistration && portals.length > 1 && (
        <div className="flex flex-col gap-md">
          {portals.map((portal) => (
            <IndirectTaxRegistrationPortalCard
              key={portal.portalId}
              portal={portal}
              isSelected={value?.portalId === portal.portalId}
              onSelect={(portalId) => patchAndResetCredentials({ portalId })}
            />
          ))}
        </div>
      )}

      {!isNewRegistration && showCredentials && (
        <IndirectTaxRegistrationCredentialsStep
          selectedPortal={selectedPortal}
          corporationId={corporationId}
          isCheckingCredentials={
            isLoadingPortals || isCheckingCredentials
          }
          credentialsFound={credentialsFound}
          registrationFields={value?.registrationFields ?? {}}
          credentialFields={value?.credentialFields ?? {}}
          onRegistrationFieldChange={(key, val) =>
            patch({
              registrationFields: {
                ...(value?.registrationFields ?? {}),
                [key]: val,
              },
            })
          }
          onCredentialFieldChange={(key, val) =>
            patch({
              credentialFields: {
                ...(value?.credentialFields ?? {}),
                [key]: val,
              },
            })
          }
        />
      )}

      {isNewRegistration &&
        registrationRegion === IndirectTaxRegion.UNITED_KINGDOM && (
        <UKActivityAndPresenceSection
          value={value?.ukActivity}
          onChange={(ukPatch) =>
            patch({ ukActivity: { ...value?.ukActivity, ...ukPatch } })
          }
        />
      )}

      {isNewRegistration &&
        registrationRegion === IndirectTaxRegion.EUROPEAN_UNION && (
        <div className="flex flex-col gap-4xl">
          <p className="txt-medium-regular text-text-tertiary">
            Help us understand your cross-border trading activity.
          </p>
          {(
            [
              {
                question: 'Do you supply or acquire goods across EU borders?',
                description:
                  'Whether you supply or acquire goods across EU borders.',
                field: 'intraCommunityTrade',
              },
              {
                question: 'Do you import or export goods outside the EU/UK?',
                description:
                  'Whether you import or export goods outside the EU/UK.',
                field: 'importsOrExportsGoods',
              },
            ] as const
          ).map(({ question, description, field }) => (
            <div key={field} className="flex flex-col gap-xl">
              <div className="flex flex-col gap-xxs">
                <p className="txt-medium-bold text-text-primary">
                  {question}
                  <span className="txt-small-medium text-utility-violet-600 ml-0.5">
                    *
                  </span>
                </p>
                <p className="txt-small-regular text-text-tertiary">
                  {description}
                </p>
              </div>
              <div className="flex items-center gap-xl">
                <Button
                  variant={
                    value?.[field] === true ? 'primary' : 'secondary'
                  }
                  size="sm"
                  onClick={() => patch({ [field]: true })}
                  type="button"
                >
                  Yes
                </Button>
                <Button
                  variant={
                    value?.[field] === false ? 'primary' : 'secondary'
                  }
                  size="sm"
                  onClick={() => patch({ [field]: false })}
                  type="button"
                >
                  No
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export { IndirectTaxRegistrationComponent };
